import React from "react";
import { useLanguage } from "@/App";
import { 
  CheckCircle, 
  X, 
  ArrowRight, 
  Shield, 
  Zap, 
  Heart, 
  Clock,
  Award,
  Users
} from "lucide-react";
import { motion } from "framer-motion";

const WhyChooseUs = () => {
  const { language } = useLanguage();

  const reasons = [
    {
      icon: Zap,
      title_en: "Speed & Quality",
      title_nl: "Snelheid & Kwaliteit",
      description_en: "We deliver faster than agencies twice our size, without compromising on quality. Most projects go live within 2-6 weeks.",
      description_nl: "Wij leveren sneller dan bureaus twee keer onze grootte, zonder kwaliteit in te leveren. De meeste projecten gaan live binnen 2-6 weken."
    },
    {
      icon: Shield,
      title_en: "Full-Stack Expertise",
      title_nl: "Full-Stack Expertise",
      description_en: "From design to deployment, AI to automation — we handle everything in-house. No subcontracting, no delays.",
      description_nl: "Van design tot deployment, AI tot automatisering — wij doen alles in-house. Geen uitbesteding, geen vertragingen."
    },
    {
      icon: Heart,
      title_en: "Personal Approach",
      title_nl: "Persoonlijke Aanpak",
      description_en: "Direct contact with Steven and the team. No account managers in between. Your vision, our execution.",
      description_nl: "Direct contact met Steven en het team. Geen account managers ertussen. Jouw visie, onze uitvoering."
    },
    {
      icon: Clock,
      title_en: "Ongoing Support",
      title_nl: "Doorlopende Support",
      description_en: "We don't disappear after launch. Maintenance packages from €99/month keep your site fast, safe and up-to-date.",
      description_nl: "Wij verdwijnen niet na lancering. Onderhoudspakketten vanaf €99/maand houden je site snel, veilig en up-to-date."
    }
  ];

  const comparison = [
    {
      feature_en: "Custom design (no templates)",
      feature_nl: "Custom design (geen templates)",
      us: true, others: false
    },
    {
      feature_en: "AI & Automation expertise",
      feature_nl: "AI & Automatisering expertise",
      us: true, others: false
    },
    {
      feature_en: "Direct contact with developers",
      feature_nl: "Direct contact met developers",
      us: true, others: false
    },
    {
      feature_en: "Fixed prices, no surprises",
      feature_nl: "Vaste prijzen, geen verrassingen",
      us: true, others: false
    },
    {
      feature_en: "Full-stack in-house team",
      feature_nl: "Full-stack in-house team",
      us: true, others: false
    },
    {
      feature_en: "Free consultation",
      feature_nl: "Gratis adviesgesprek",
      us: true, others: true
    },
    {
      feature_en: "Post-launch support",
      feature_nl: "Support na lancering",
      us: true, others: true
    },
    {
      feature_en: "Delivery within 2-6 weeks",
      feature_nl: "Oplevering binnen 2-6 weken",
      us: true, others: false
    }
  ];

  const getTitle = (r) => language === "nl" ? r.title_nl : r.title_en;
  const getDesc = (r) => language === "nl" ? r.description_nl : r.description_en;
  const getFeature = (c) => language === "nl" ? c.feature_nl : c.feature_en;

  return (
    <section
      id="why-us"
      data-testid="why-choose-us-section"
      className="py-32 bg-[#030303] relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,107,0,0.06),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-5 py-2 mb-6">
            <Award size={16} className="text-[#FF6B00]" />
            <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-[0.2em]">
              {language === "nl" ? "Waarom Wij" : "Why Us"}
            </span>
          </span>
          <h2
            data-testid="why-choose-us-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {language === "nl" ? "Waarom " : "Why "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
              EG Solutions?
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {language === "nl"
              ? "Geen standaard bureau. Wij combineren creativiteit, technologie en persoonlijke aandacht voor resultaat dat je voelt."
              : "Not your average agency. We combine creativity, technology and personal attention for results you can feel."}
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`why-reason-${index}`}
              className="group bg-[#0A0A0A] border border-white/5 p-8 hover:border-[#FF6B00]/30 transition-all"
            >
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-[#FF6B00]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <reason.icon size={28} className="text-[#FF6B00]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {getTitle(reason)}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed">
                    {getDesc(reason)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-white text-center mb-10" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {language === "nl" ? "EG Solutions vs. Andere Bureaus" : "EG Solutions vs. Other Agencies"}
          </h3>

          <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-white/5">
              <div className="p-4 md:p-6 text-sm font-bold text-zinc-400 uppercase tracking-wider">
                {language === "nl" ? "Kenmerk" : "Feature"}
              </div>
              <div className="p-4 md:p-6 text-center">
                <span className="text-[#FF6B00] font-bold text-sm uppercase tracking-wider">EG Solutions</span>
              </div>
              <div className="p-4 md:p-6 text-center">
                <span className="text-zinc-500 font-bold text-sm uppercase tracking-wider">
                  {language === "nl" ? "Anderen" : "Others"}
                </span>
              </div>
            </div>

            {/* Table Rows */}
            {comparison.map((row, index) => (
              <div
                key={index}
                data-testid={`comparison-row-${index}`}
                className="grid grid-cols-3 border-t border-white/5 hover:bg-white/[0.02] transition-colors"
              >
                <div className="p-4 md:p-6 text-sm text-zinc-300">
                  {getFeature(row)}
                </div>
                <div className="p-4 md:p-6 flex justify-center">
                  <CheckCircle size={20} className="text-[#FF6B00]" />
                </div>
                <div className="p-4 md:p-6 flex justify-center">
                  {row.others
                    ? <CheckCircle size={20} className="text-zinc-600" />
                    : <X size={20} className="text-zinc-700" />
                  }
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="#contact"
            data-testid="why-us-cta"
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#FF6B00] to-[#FF4500] text-white px-10 py-5 text-sm font-bold tracking-wide hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] transition-all"
          >
            {language === "nl" ? "Gratis Adviesgesprek Plannen" : "Schedule Free Consultation"}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
